
import React from 'react';
import { format, parseISO } from 'date-fns';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Trash2, MapPin, Calendar, Phone, CreditCard, FileText, User, Users, Edit } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

interface Hotel {
  id: string;
  name: string;
  address: string;
  checkIn: string;
  checkOut: string;
  confirmation: string;
  notes: string;
  price: number;
  phone: string;
  addedBy?: string; 
  roommates?: Array<{id: string, name: string}>;
}

interface HotelCardProps {
  hotel: Hotel;
  onRemove: (id: string) => void;
  onEdit: (id: string) => void;
}

export const HotelCard: React.FC<HotelCardProps> = ({ hotel, onRemove, onEdit }) => {
  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    try {
      // This ensures we're parsing the date correctly without timezone issues
      const date = parseISO(dateString);
      return format(date, 'MMM d, yyyy');
    } catch (e) {
      console.error('Error formatting date:', e, dateString);
      return dateString;
    }
  };

  // Ensure roommates is always an array
  const roommates = Array.isArray(hotel.roommates) ? hotel.roommates : [];
  
  console.log('Rendering hotel card with roommates:', roommates);

  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-2 bg-glowingTeal/10">
        <CardTitle className="flex justify-between items-start">
          <span className="text-lg">{hotel.name}</span>
          <div className="flex gap-1">
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-8 w-8 text-blue-600 hover:text-blue-800 hover:bg-blue-100"
              onClick={() => onEdit(hotel.id)}
            >
              <Edit className="h-4 w-4" />
            </Button>
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-8 w-8 text-red-600 hover:text-red-800 hover:bg-red-100"
              onClick={() => onRemove(hotel.id)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-4 space-y-3">
        {hotel.address && (
          <div className="flex items-start">
            <MapPin className="h-4 w-4 mt-0.5 mr-2 text-muted-foreground" />
            <span className="text-sm">{hotel.address}</span>
          </div>
        )}
        
        {(hotel.checkIn || hotel.checkOut) && (
          <div className="flex items-start">
            <Calendar className="h-4 w-4 mt-0.5 mr-2 text-muted-foreground" />
            <div className="text-sm">
              {hotel.checkIn && <div>Check-in: {formatDate(hotel.checkIn)}</div>}
              {hotel.checkOut && <div>Check-out: {formatDate(hotel.checkOut)}</div>}
            </div>
          </div>
        )}
        
        {hotel.phone && (
          <div className="flex items-start">
            <Phone className="h-4 w-4 mt-0.5 mr-2 text-muted-foreground" />
            <span className="text-sm">{hotel.phone}</span>
          </div>
        )}
        
        {hotel.price > 0 && (
          <div className="flex items-start">
            <CreditCard className="h-4 w-4 mt-0.5 mr-2 text-muted-foreground" />
            <span className="text-sm">${hotel.price} per night</span>
          </div>
        )}
        
        {hotel.confirmation && (
          <div className="flex items-start">
            <FileText className="h-4 w-4 mt-0.5 mr-2 text-muted-foreground" />
            <span className="text-sm">Confirmation: {hotel.confirmation}</span>
          </div>
        )}
        
        {hotel.addedBy && (
          <div className="flex items-start">
            <User className="h-4 w-4 mt-0.5 mr-2 text-muted-foreground" />
            <span className="text-sm">Traveler: {hotel.addedBy}</span>
          </div>
        )}
        
        {roommates.length > 0 && (
          <div className="flex items-start">
            <Users className="h-4 w-4 mt-0.5 mr-2 text-muted-foreground" />
            <div className="flex flex-col w-full">
              <span className="text-sm mb-1">Roommates:</span>
              <div className="flex flex-wrap gap-1.5">
                {roommates.map(roommate => (
                  <Badge key={roommate.id} variant="secondary" className="text-xs">
                    {roommate.name}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        )}
      </CardContent>
      {hotel.notes && (
        <CardFooter className="border-t bg-muted/20 pt-3">
          <div className="text-sm text-muted-foreground">{hotel.notes}</div>
        </CardFooter>
      )}
    </Card>
  );
};
